import React from 'react';
import { useObjectSchema, useObjectSchemas } from '../../hooks/useObjectSchema';
import {
  JsonSchema,
  ObjectLookup,
  SchemaSourceObject,
  fieldsToJsonSchema,
  referencedObjectIds,
} from '../../utils/jsonSchema';
import { SchemaView, SchemaViewProps } from './SchemaView';

export interface ObjectSchemaViewProps extends Omit<SchemaViewProps, 'schema' | 'loading' | 'caption'> {
  /** Custom object whose runtime schema (what the model receives) should be shown. */
  objectId?: string | null;
  /** Schema built in the browser, shown while loading or when the runtime schema is unavailable. */
  fallbackSchema?: JsonSchema | null;
  /** Show the fallback instead of the runtime schema, e.g. while there are unsaved edits. */
  preferFallback?: boolean;
  showSource?: boolean;
}

export const ObjectSchemaView: React.FC<ObjectSchemaViewProps> = ({
  objectId,
  fallbackSchema,
  preferFallback = false,
  showSource = true,
  ...rest
}) => {
  const { schema: runtime, loading, failed } = useObjectSchema(objectId, !preferFallback);
  const schema = (!preferFallback && runtime) || fallbackSchema || null;

  let caption: React.ReactNode;
  if (showSource) {
    if (preferFallback && fallbackSchema) caption = 'Preview of unsaved changes';
    else if (runtime) caption = 'Schema the model receives at runtime';
    else if (failed && fallbackSchema) caption = 'Built from the object definition (runtime schema unavailable)';
  }

  return <SchemaView {...rest} schema={schema} loading={!preferFallback && loading} caption={caption} />;
};

export interface DraftObjectSchemaViewProps extends Omit<ObjectSchemaViewProps, 'objectId' | 'fallbackSchema' | 'preferFallback'> {
  /** The object as currently edited; `id` is set once it has been saved. */
  object: SchemaSourceObject;
  /** Other objects, used to expand references the runtime schema can't provide. */
  objects?: ObjectLookup;
  /** The editor has changes that aren't saved yet. */
  unsaved: boolean;
}

/** Runtime schema of a saved object, or a schema built from the editor while there are unsaved edits. */
export const DraftObjectSchemaView: React.FC<DraftObjectSchemaViewProps> = ({ object, objects = {}, unsaved, ...rest }) => {
  const fields = object.fields || [];
  const draft = unsaved || !object.id;
  const referenceSchemas = useObjectSchemas(referencedObjectIds(fields), draft);
  const fallbackSchema = fieldsToJsonSchema(fields, objects, {
    description: object.description || `${object.display_name || object.name || 'Object'} object`,
    visiting: new Set(object.id ? [object.id] : []),
    referenceSchemas,
  });
  return <ObjectSchemaView {...rest} objectId={object.id} fallbackSchema={fallbackSchema} preferFallback={draft} />;
};

export default ObjectSchemaView;
